import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface AiModel {
  id: string;
  modelId: string;
  provider: string;
  displayName: string | null;
  tier: string;
  inputCostPer1M: string | null;
  outputCostPer1M: string | null;
  isAvailable: boolean;
  isActive: boolean;
  lastSeenAt: string;
  createdAt: string;
}

const TIER_COLORS: Record<string, string> = {
  flagship: 'bg-purple-100 text-purple-700',
  budget: 'bg-blue-100 text-blue-700',
  cheapest: 'bg-green-100 text-green-700',
  unknown: 'bg-gray-100 text-gray-500',
};

export default function Models() {
  const [models, setModels] = useState<AiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');

  async function load() {
    setLoading(true);
    try {
      const data = await api.getModels();
      setModels(data.models as AiModel[]);
    } catch (e) {
      setMessage(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const result = await api.refreshModels();
      setMessage(`Refresh complete: ${result.newModels.length} new models, ${result.updatedCount} updated`);
      load();
    } catch (e) {
      setMessage(String(e));
    } finally {
      setRefreshing(false);
    }
  }

  async function handleUpdateTier(model: AiModel, tier: string) {
    try {
      await api.updateModel(model.id, { tier });
      load();
    } catch (e) {
      setMessage(String(e));
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">AI Models</h1>
        <button
          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh from OpenAI'}
        </button>
      </div>

      {message && <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded text-sm">{message}</div>}

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : models.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg mb-2">No models discovered yet</p>
          <p className="text-sm">Click "Refresh from OpenAI" to discover available models</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded border">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-4 py-2">Model</th>
                <th className="px-4 py-2">Provider</th>
                <th className="px-4 py-2">Tier</th>
                <th className="px-4 py-2">Input $/1M</th>
                <th className="px-4 py-2">Output $/1M</th>
                <th className="px-4 py-2">Available</th>
                <th className="px-4 py-2">Last Seen</th>
                <th className="px-4 py-2">Discovered</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m) => (
                <tr key={m.id} className={`border-t hover:bg-gray-50 ${!m.isAvailable ? 'opacity-40' : ''}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-xs">{m.displayName || m.modelId}</div>
                    <div className="text-xs text-gray-400 font-mono">{m.modelId}</div>
                  </td>
                  <td className="px-4 py-2 text-xs">{m.provider}</td>
                  <td className="px-4 py-2">
                    <select
                      className={`text-xs px-2 py-0.5 rounded font-medium border-0 cursor-pointer ${TIER_COLORS[m.tier] || TIER_COLORS.unknown}`}
                      value={m.tier}
                      onChange={(e) => handleUpdateTier(m, e.target.value)}
                    >
                      <option value="flagship">Flagship</option>
                      <option value="budget">Budget</option>
                      <option value="cheapest">Cheapest</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </td>
                  <td className="px-4 py-2 text-xs tabular-nums">{m.inputCostPer1M ? `$${Number(m.inputCostPer1M).toFixed(2)}` : '—'}</td>
                  <td className="px-4 py-2 text-xs tabular-nums">{m.outputCostPer1M ? `$${Number(m.outputCostPer1M).toFixed(2)}` : '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs ${m.isAvailable ? 'text-green-600' : 'text-red-400'}`}>
                      {m.isAvailable ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">{new Date(m.lastSeenAt).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{new Date(m.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
